import React, { useState } from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";
import ConfirmContext, { DialogType } from "@/context/confirm/ConfirmContext";

// 기존 ConfirmDialog가 사용하는 상태 타입
type ConfirmState = {
    message: string;
    onClickOK: () => void;
    onClickCancel: () => void;
};

// ConfirmPopup이 사용하는 상태 타입
type PopupState = {
    title: string;
    highlightText?: string;
    type: DialogType;
    onConfirm: () => void;
    onClose: () => void;
};

/**
 * 통합된 확인 다이얼로그 컴포넌트
 * ConfirmDialog와 ConfirmPopup의 기능을 모두 제공합니다.
 */
const EnhancedConfirmDialog = ({ children }: { children: React.ReactNode }) => {
    // 기존 ConfirmDialog 상태
    const [confirmState, setConfirmState] = useState<ConfirmState>();

    // ConfirmPopup 상태
    const [popupState, setPopupState] = useState<PopupState>();

    // 기존 ConfirmDialog 메서드 (기존 코드 호환성 유지)
    const confirm = (message?: string): Promise<boolean> => {
        return new Promise((resolve) => {
            setConfirmState({
                message: message ?? "",
                onClickOK: () => {
                    setConfirmState(undefined);
                    resolve(true);
                },
                onClickCancel: () => {
                    setConfirmState(undefined);
                    resolve(false);
                }
            });
        });
    };

    // 새로운 ConfirmPopup 스타일의 다이얼로그 메서드
    const showPopup = (
        title: string,
        options?: {
            highlightText?: string;
            type?: DialogType;
            onConfirm?: () => void;
        }
    ): Promise<boolean> => {
        return new Promise((resolve) => {
            setPopupState({
                title,
                highlightText: options?.highlightText,
                type: options?.type || "confirm", // 기본값은 confirm
                onConfirm: () => {
                    // 콜백을 먼저 호출하고 나서 상태를 변경
                    if (options?.onConfirm) {
                        options.onConfirm();
                    }
                    setPopupState(undefined);
                    resolve(true);
                },
                onClose: () => {
                    setPopupState(undefined);
                    resolve(false);
                }
            });
        });
    };

    // 렌더링 메서드 - 기존 스타일의 확인창
    const renderLegacyConfirm = () => {
        if (!confirmState) return null;

        return (
            <LegacyDialogContainer>
                <LegacyOverlay onClickCapture={(e) => e.stopPropagation()} />
                <LegacyDialog>
                    <h2 className="title"></h2>
                    <LegacyText>{confirmState.message}</LegacyText>
                    <LegacyButtons>
                        <LegacyButton onClick={confirmState.onClickCancel}>cancel</LegacyButton>
                        <LegacyButton onClick={confirmState.onClickOK} autoFocus>
                            ok
                        </LegacyButton>
                    </LegacyButtons>
                </LegacyDialog>
            </LegacyDialogContainer>
        );
    };

    // 렌더링 메서드 - 새 스타일의 팝업
    const renderPopup = () => {
        if (!popupState) return null;

        // 강조 텍스트가 있으면 타이틀에서 해당 부분만 강조하여 표시
        const renderTitle = () => {
            if (!popupState.highlightText) return popupState.title;

            const parts = popupState.title.split(popupState.highlightText);
            return (
                <>
                    {parts[0]}
                    <HighlightSpan>{popupState.highlightText}</HighlightSpan>
                    {parts[1]}
                </>
            );
        };

        return (
            <Backdrop onClick={popupState.type === "alert" ? undefined : popupState.onClose}>
                <PopupContainer onClick={(e) => e.stopPropagation()}>
                    <IconContainer>
                        <AlertIcon>!</AlertIcon>
                    </IconContainer>

                    <TitleContainer>{renderTitle()}</TitleContainer>

                    <ButtonContainer>
                        {popupState.type === "confirm" && (
                            <CancelButton onClick={popupState.onClose}>취소</CancelButton>
                        )}
                        <ConfirmButton onClick={popupState.onConfirm} $isFullWidth={popupState.type === "alert"}>
                            확인
                        </ConfirmButton>
                    </ButtonContainer>
                </PopupContainer>
            </Backdrop>
        );
    };

    return (
        <ConfirmContext.Provider value={{ confirm, showPopup }}>
            {children}
            {renderLegacyConfirm()}
            {renderPopup()}
        </ConfirmContext.Provider>
    );
};

// 기존 다이얼로그 스타일 컴포넌트
const LegacyDialogContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const LegacyOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
`;

const LegacyDialog = styled.div`
    background: white;
    border-radius: 4px;
    padding: 20px;
    min-width: 300px;
    z-index: 1001;
`;

const LegacyText = styled.div`
    margin: 20px 0;
`;

const LegacyButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const LegacyButton = styled.button`
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;

    &:focus {
        outline: 2px solid #4000c7;
    }
`;

// 새 팝업 스타일 컴포넌트
const Backdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const PopupContainer = styled.div`
    width: 90%;
    max-width: 350px;
    background-color: ${palette.white};
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 24px 16px;
`;

const IconContainer = styled.div`
    margin-bottom: 16px;
`;

const AlertIcon = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: ${palette.gray[100]};
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 32px;
    font-weight: bold;
    color: ${palette.white};
`;

const TitleContainer = styled.div`
    ${typography.title3}
    color: ${palette.gray[900]};
    text-align: center;
    margin-bottom: 24px;
    word-break: keep-all;
    line-height: 1.5;
`;

const HighlightSpan = styled.span`
    color: ${palette.primary};
    font-weight: bold;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
`;

const Button = styled.button`
    flex: 1;
    padding: 12px 0;
    border-radius: 8px;
    border: none;
    ${typography.button}
    cursor: pointer;
`;

const CancelButton = styled(Button)`
    background-color: ${palette.gray[200]};
    color: ${palette.gray[800]};
`;

const ConfirmButton = styled(Button)<{ $isFullWidth?: boolean }>`
    background-color: ${palette.primary};
    color: ${palette.white};
    width: ${(props) => (props.$isFullWidth ? "100%" : "auto")};
`;

export default EnhancedConfirmDialog;
